import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import {
  EyeOutlined,
  LikeOutlined,
  DislikeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { useDataGridDateRangeFilter } from '../DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { useDataGridTextFilter } from '../DataGrid/filters/useDataGridTextFilter.jsx';
import { dataGridTextSorter } from '../DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../DataGrid/DataGrid.jsx';
import IconButton from '../controls/IconButton/IconButton.jsx';
import { Spinner } from '../index.jsx';
import IpChangeRequestDetailModal from '../../pages/CMSPages/IngProjects/IngProjectsChangeRequests/IpChangeRequestDetailModal.jsx';

const IpChangeRequestsTable = ({
  changeRequests,
  isLoading = false,
  onApprove,
  onReject,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [detailModalId, setDetailModalId] = useState(null);

  const lastNameFilterProps = useDataGridTextFilter(['student', 'person', 'last_name']);
  const firstNameFilterProps = useDataGridTextFilter(['student', 'person', 'first_name']);
  const studyProgramFilterProps = useDataGridTextFilter(['study_program', 'shortcut']);
  const createdAtFilterProps = useDataGridDateRangeFilter('created_at');

  const resolveStateKey = (record) => {
    const state = record?.state;

    if (state === null || state === undefined) {
      return 'pending';
    }

    if (typeof state === 'string') {
      const trimmedState = state.trim();
      if (trimmedState === '') {
        return 'pending';
      }

      if (trimmedState === 'Čaká na schválenie') {
        return 'pending';
      }

      if (trimmedState === 'Schválená') {
        return 'approved';
      }

      if (trimmedState === 'Zamietnutá') {
        return 'rejected';
      }

      if (trimmedState === 'Archivovaná') {
        return 'archived';
      }

      if (trimmedState === '0') {
        return 'pending';
      }

      if (trimmedState === '1') {
        return 'approved';
      }

      if (trimmedState === '2') {
        return 'rejected';
      }

      if (trimmedState === '3') {
        return 'archived';
      }
    }

    if (typeof state === 'number') {
      if (state === 0) {
        return 'pending';
      }
      if (state === 1) {
        return 'approved';
      }
      if (state === 2) {
        return 'rejected';
      }
      if (state === 3) {
        return 'archived';
      }
    }

    return 'pending';
  };

  const getStateTag = (record) => {
    const stateKey = resolveStateKey(record);
    const approvalCount = Number(record?.approval_count ?? 0);

    if (stateKey === 'rejected') {
      return <Tag color="red">{t('Zamietnutá')}</Tag>;
    }

    if (stateKey === 'approved') {
      return <Tag color="green">{t('Schválená')}</Tag>;
    }

    if (stateKey === 'archived') {
      return <Tag color="default">{t('Archivovaná')}</Tag>;
    }

    if (approvalCount > 0) {
      return <Tag color="pink">{t('Schválená')} {approvalCount}/3</Tag>;
    }

    return <Tag color="orange">{t('Čaká na schválenie')}</Tag>;
  };

  const columns = useMemo(
    () => [
      {
        title: t('Priezvisko'),
        dataIndex: ['student', 'person', 'last_name'],
        ellipsis: true,
        render: (_, row) => row.student?.person?.last_name || '–',
        sorter: (a, b) => {
          const nameA = a.student?.person?.last_name || '';
          const nameB = b.student?.person?.last_name || '';
          return nameA.localeCompare(nameB);
        },
        ...lastNameFilterProps,
      },
      {
        title: t('Meno'),
        dataIndex: ['student', 'person', 'first_name'],
        ellipsis: true,
        render: (_, row) => row.student?.person?.first_name || '–',
        sorter: (a, b) => {
          const nameA = a.student?.person?.first_name || '';
          const nameB = b.student?.person?.first_name || '';
          return nameA.localeCompare(nameB);
        },
        ...firstNameFilterProps,
      },
      {
        title: t('Pôvodný projekt'),
        dataIndex: 'original_project',
        ellipsis: true,
        width: '22%',
        render: (_, row) => row.original_project?.name || '–',
        sorter: dataGridTextSorter('original_project.name'),
      },
      {
        title: t('Nový projekt'),
        dataIndex: 'new_project',
        ellipsis: true,
        width: '22%',
        render: (_, row) => row.new_project?.name || '–',
        sorter: dataGridTextSorter('new_project.name'),
      },
      {
        title: t('Št. program'),
        dataIndex: ['study_program', 'shortcut'],
        ellipsis: true,
        render: (_, row) => row.study_program?.shortcut || '–',
        sorter: (a, b) => {
          const valueA = a.study_program?.shortcut || '';
          const valueB = b.study_program?.shortcut || '';
          return valueA.localeCompare(valueB);
        },
        ...studyProgramFilterProps,
      },
      {
        title: t('Stav'),
        dataIndex: 'state',
        render: (_, row) => getStateTag(row),
        sorter: (a, b) => {
          const stateOrder = { pending: 0, approved: 1, rejected: 2, archived: 3 };
          const aValue = stateOrder[resolveStateKey(a)] ?? 99;
          const bValue = stateOrder[resolveStateKey(b)] ?? 99;
          return aValue - bValue;
        },
        filters: [
          { text: t('Čaká na schválenie'), value: 'pending' },
          { text: t('Schválená'), value: 'approved' },
          { text: t('Zamietnutá'), value: 'rejected' },
          { text: t('Archivovaná'), value: 'archived' },
        ],
        onFilter: (value, record) => resolveStateKey(record) === value,
      },
      {
        title: t('Dátum žiadosti'),
        dataIndex: 'created_at',
        render: (_, row) => {
          if (!row.created_at) return '–';
          return dayjs(row.created_at).format('DD.MM.YYYY HH:mm');
        },
        sorter: (a, b) => {
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          return dateB - dateA;
        },
        ...createdAtFilterProps,
      },
    ],
    [t, lastNameFilterProps, firstNameFilterProps, studyProgramFilterProps, createdAtFilterProps],
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <DataGrid
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={Array.isArray(changeRequests) ? changeRequests : []}
        onRowClick={(record) => setDetailModalId(record.id)}
        onDelete={onDelete ? (row) => onDelete(row.id) : undefined}
        deleteCondition={(row) => resolveStateKey(row) === 'pending'}
        customSettings={(row) => (
          <div style={{ display: 'flex' }}>
            <IconButton
              icon={<EyeOutlined />}
              tooltip={t('Náhľad žiadosti')}
              onClick={(e) => {
                e.stopPropagation();
                setDetailModalId(row.id);
              }}
            />
            {row.can_approve && onApprove && (
              <IconButton
                icon={<LikeOutlined />}
                tooltip={t('Schváliť')}
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(row.id);
                }}
              />
            )}
            {row.can_approve && onReject && (
              <IconButton
                icon={<DislikeOutlined />}
                tooltip={t('Zamietnuť')}
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(row.id);
                }}
              />
            )}
          </div>
        )}
      />

      <IpChangeRequestDetailModal
        changeRequestId={detailModalId}
        visible={detailModalId !== null}
        onClose={() => setDetailModalId(null)}
        onApprove={onApprove}
        onReject={onReject}
      />
    </>
  );
};

IpChangeRequestsTable.propTypes = {
  changeRequests: PropTypes.array,
  isLoading: PropTypes.bool,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onDelete: PropTypes.func,
};

export default IpChangeRequestsTable;
